import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheckCircle, FiPhone, FiMessageCircle } = FiIcons;

const SuccessScreen = ({ formData, onStartOver }) => {
  const whatsappNumber = "+918300615054";
  const message = "Hi! I have added my delivery location through your registration form. Please share the QR code for the payment. Thank you!";
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;

  // Auto-redirect to WhatsApp after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      window.open(whatsappUrl, '_blank');
    }, 3000);

    return () => clearTimeout(timer);
  }, [whatsappUrl]);

  const handleWhatsAppClick = () => {
    window.open(whatsappUrl, '_blank');
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
      className="text-center w-full"
    >
      <motion.div
        animate={{ scale: [1, 1.2, 1] }}
        transition={{ duration: 1, repeat: Infinity }}
        className="text-8xl mb-6"
      >
        🎉
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="mb-8"
      >
        <SafeIcon icon={FiCheckCircle} className="text-6xl text-green-500 mx-auto mb-4" />
        <h2 className="text-3xl font-bold text-brand-primary mb-4">
          Registration Successful! 🍛
        </h2>
        <p className="text-brand-primary/70 text-lg mb-6">
          Thank you {formData.name}! We've received your details.
        </p>
      </motion.div>

      {/* Auto-redirect notification */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-gradient-to-r from-green-50 to-green-100 p-6 rounded-2xl mb-6 border-2 border-green-200"
      >
        <div className="flex items-center justify-center space-x-3 mb-4">
          <SafeIcon icon={FiMessageCircle} className="text-green-600 text-3xl animate-bounce-gentle" />
          <div>
            <h3 className="text-green-800 font-bold text-lg">Opening WhatsApp...</h3>
            <p className="text-green-700 text-sm">You'll be redirected automatically in 3 seconds</p>
          </div>
        </div>
        
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleWhatsAppClick}
          className="w-full py-4 px-6 rounded-2xl font-bold text-lg bg-green-600 text-white hover:bg-green-700 transition-all duration-300 flex items-center justify-center space-x-3 shadow-lg"
        >
          <SafeIcon icon={FiMessageCircle} className="text-xl" />
          <span>Open WhatsApp Now</span>
        </motion.button>
        
        <p className="text-green-600 text-xs mt-3">
          Message: "Hi! I have added my delivery location through your registration form. Please share the QR code for the payment. Thank you!"
        </p>
      </motion.div>

      <div className="bg-gradient-to-r from-brand-cream to-brand-white p-6 rounded-2xl mb-8 border border-brand-gray">
        <h3 className="text-brand-primary font-semibold mb-4">What happens next?</h3>
        <div className="space-y-4 text-left">
          <div className="flex items-start space-x-3">
            <div className="w-6 h-6 bg-brand-orange rounded-full flex items-center justify-center text-brand-white text-sm font-bold flex-shrink-0 mt-0.5">1</div>
            <p className="text-brand-primary/80">Send the WhatsApp message to get your payment QR code</p>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-6 h-6 bg-brand-orange rounded-full flex items-center justify-center text-brand-white text-sm font-bold flex-shrink-0 mt-0.5">2</div>
            <p className="text-brand-primary/80">Complete payment using the QR code we'll send</p>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-6 h-6 bg-brand-orange rounded-full flex items-center justify-center text-brand-white text-sm font-bold flex-shrink-0 mt-0.5">3</div>
            <p className="text-brand-primary/80">We'll verify your location and delivery area</p>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-6 h-6 bg-brand-orange rounded-full flex items-center justify-center text-brand-white text-sm font-bold flex-shrink-0 mt-0.5">4</div>
            <p className="text-brand-primary/80">Start enjoying fresh, homemade tiffins delivered daily!</p>
          </div>
        </div>
      </div>

      <div className="bg-brand-primary/5 p-6 rounded-2xl mb-6">
        <div className="flex items-center justify-center space-x-2 mb-3">
          <SafeIcon icon={FiPhone} className="text-brand-primary text-xl" />
          <p className="text-brand-primary font-semibold">Need help?</p>
        </div>
        <p className="text-brand-primary/70 text-sm">
          WhatsApp us at <span className="font-semibold">+91 8300615054</span>
        </p>
      </div>

      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={onStartOver}
        className="w-full py-4 px-6 rounded-2xl font-semibold text-lg bg-brand-white text-brand-primary border-2 border-brand-primary hover:bg-brand-cream transition-all duration-300"
      >
        Register Another Person
      </motion.button>
    </motion.div>
  );
};

export default SuccessScreen;